import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { getObjectId, type Order } from '@hero/schema';
import { useMemberListApi, useOrderUpdateApi } from '@hero/api-hooks';
import { Modal } from '../../../app/Modal';
import { Button } from '../../../ui/Button';
import { SelectAuto, type SelectAutoOption } from '../../../app/Select/SelectAuto';

export interface OrderChangeOwnerModalProps {
  order: Order;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Reassign `order.orderOwner` to an org member with a linked user account.
 * Backend stamps name + phone via `orderOwnerUserId` on OrderUpdateAPI.
 */
export const OrderChangeOwnerModal: React.FC<OrderChangeOwnerModalProps> = ({
  order,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation(['m-order', 'c-main']);
  const { data: members = [], isLoading } = useMemberListApi({}, { enabled: isOpen });
  const [userId, setUserId] = useState<string>('');

  const options: SelectAutoOption[] = useMemo(
    () =>
      members
        .filter(m => !!m.userId)
        .map(m => ({
          value: String(getObjectId(m.userId!)),
          label: m.name || m.phone,
          description: m.phone,
        })),
    [members],
  );

  useEffect(() => {
    if (!isOpen) return;
    const current = order.orderOwner?._id
      ? String(getObjectId(order.orderOwner._id))
      : '';
    setUserId(current);
  }, [isOpen, order.orderOwner?._id]);

  const updateOrder = useOrderUpdateApi({
    onSuccess: () => {
      toast.success(t('m-order:toast.success.order_owner_updated'));
      onClose();
    },
    onError: (error: Error) => {
      toast.error(
        t('m-order:toast.error.order_owner_update', { message: error.message }),
      );
    },
  });

  const handleConfirm = () => {
    if (!userId || updateOrder.isPending) return;
    updateOrder.mutate({
      _id: getObjectId(order._id),
      orderOwnerUserId: getObjectId(userId),
    });
  };

  const handleClose = () => {
    if (updateOrder.isPending) return;
    onClose();
  };

  const canConfirm = !!userId && !updateOrder.isPending;

  return (
    <Modal
      btnTitle=""
      isOpen={isOpen}
      onClose={handleClose}
      modalTitle={t('m-order:modal.change_order_owner.title')}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t('m-order:modal.change_order_owner.description')}
        </p>

        {!isLoading && options.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('m-order:modal.change_order_owner.no_members')}
          </p>
        ) : (
          <SelectAuto
            options={options}
            value={userId}
            onChange={value => setUserId(String(value || ''))}
            placeholder={t('m-order:modal.change_order_owner.member_placeholder')}
            label={t('m-order:modal.change_order_owner.member_label')}
            disabled={updateOrder.isPending || isLoading}
          />
        )}

        <div className="flex gap-2 pt-2 justify-end">
          <Button type="button" variant="outline" onClick={handleClose} disabled={updateOrder.isPending}>
            {t('c-main:crud_buttons.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={!canConfirm || options.length === 0}>
            {updateOrder.isPending
              ? t('m-order:modal.change_order_owner.saving')
              : t('m-order:modal.change_order_owner.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
